<template>
  <div class="notice-page page-wrapper">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">参会须知</h1>
        <p class="page-subtitle">Attendee Information</p>
      </div>
    </div>

    <div class="page-content">
      <div class="container">
        <!-- 须知导航 -->
        <div class="notice-nav">
          <div 
            v-for="(item, index) in navItems" 
            :key="index"
            class="nav-item"
            :class="{ 'active': activeTab === item.type }"
            @click="activeTab = item.type"
          >
            <i :class="item.icon"></i>
            <span>{{ item.name }}</span>
          </div>
        </div>

        <!-- 会场信息 -->
        <div v-show="activeTab === 'venue'" class="notice-section">
          <div class="notice-card">
            <h3 class="card-title"><i class="el-icon-office-building"></i> 会场信息</h3>
            <div class="card-content" v-loading="loading.venue">
              <div v-for="venue in venueList" :key="venue.id" class="venue-item">
                <h4>{{ venue.name }}</h4>
                <p><strong>地址：</strong>{{ venue.address }}</p>
                <p v-if="venue.capacity"><strong>容纳人数：</strong>{{ venue.capacity }}人</p>
                <p v-if="venue.description">{{ venue.description }}</p>
                <p v-if="venue.contact_phone"><strong>联系电话：</strong>{{ venue.contact_phone }}</p>
              </div>
              <el-empty v-if="!loading.venue && venueList.length === 0" description="暂无会场信息"></el-empty>
            </div>
          </div>
        </div>

        <!-- 酒店信息 -->
        <div v-show="activeTab === 'hotel'" class="notice-section">
          <div class="notice-card">
            <h3 class="card-title"><i class="el-icon-house"></i> 推荐酒店</h3>
            <div class="card-content" v-loading="loading.hotel">
              <div class="hotel-list">
                <div v-for="hotel in hotelList" :key="hotel.id" class="hotel-item">
                  <div class="hotel-level">
                    <el-tag :type="hotel.star_level >= 5 ? 'danger' : hotel.star_level >= 4 ? 'warning' : 'info'">
                      {{ hotel.star_level }}星
                    </el-tag>
                  </div>
                  <div class="hotel-info">
                    <h4>{{ hotel.name }}</h4>
                    <p><i class="el-icon-location-outline"></i> {{ hotel.address }}</p>
                    <!-- <p v-if="hotel.distance"><i class="el-icon-map-location"></i> 距会场{{ hotel.distance }}</p> -->
                    <!-- <p v-if="hotel.price_range" class="hotel-price"><strong>协议价：{{ hotel.price_range }}</strong></p> -->
                    <p v-if="hotel.phone"><i class="el-icon-phone"></i> {{ hotel.phone }}</p>
                    <!-- <el-tag v-if="hotel.has_shuttle" type="success" size="mini">接驳车</el-tag> -->
                  </div>
                </div>
              </div>
              <el-empty v-if="!loading.hotel && hotelList.length === 0" description="暂无酒店信息"></el-empty>
            </div>
          </div>
        </div>

        <!-- 景点指南 -->
        <div v-show="activeTab === 'scenic'" class="notice-section">
          <div class="notice-card">
            <h3 class="card-title"><i class="el-icon-place"></i> 景点指南</h3>
            <div class="card-content" v-loading="loading.attraction">
              <div class="scenic-grid">
                <div v-for="attraction in attractionList" :key="attraction.id" class="scenic-item">
                  <div class="scenic-icon"><i class="el-icon-s-opportunity"></i></div>
                  <div class="scenic-info">
                    <h5>{{ attraction.name }}</h5>
                    <p>{{ attraction.description }}</p>
                    <p v-if="attraction.address" class="scenic-address">
                      <i class="el-icon-location"></i> {{ attraction.address }}
                    </p>
                    <!-- <p v-if="attraction.ticket_price" class="scenic-price">
                      <i class="el-icon-ticket"></i> 门票：{{ attraction.ticket_price }}
                    </p>
                    <p v-if="attraction.open_time" class="scenic-time">
                      <i class="el-icon-time"></i> {{ attraction.open_time }}
                    </p> -->
                  </div>
                </div>
              </div>
              <el-empty v-if="!loading.attraction && attractionList.length === 0" description="暂无景点信息"></el-empty>
            </div>
          </div>
        </div>

        <!-- 参会者手册 -->
        <div v-show="activeTab === 'handbook'" class="notice-section">
          <div class="notice-card">
            <h3 class="card-title"><i class="el-icon-notebook-2"></i> 参会者手册</h3>
            <div class="card-content" v-loading="loading.handbook">
              <div v-for="item in handbookList" :key="item.id" class="handbook-section">
                <h4>{{ item.title }}</h4>
                <div v-html="item.content"></div>
                <!-- <a v-if="item.file_url" :href="item.file_url" target="_blank" class="download-link">
                  <i class="el-icon-download"></i> 下载附件
                </a> -->
              </div>
              <el-empty v-if="!loading.handbook && handbookList.length === 0" description="暂无手册内容"></el-empty>
            </div>
          </div>
        </div>

        <!-- 演讲者须知 -->
        <div v-show="activeTab === 'speaker'" class="notice-section">
          <div class="notice-card">
            <h3 class="card-title"><i class="el-icon-microphone"></i> 演讲者须知</h3>
            <div class="card-content" v-loading="loading.speakerGuide">
              <div v-for="item in speakerGuideList" :key="item.id" class="handbook-section">
                <h4>{{ item.title }}</h4>
                <div v-html="item.content"></div>
                <div v-if="item.tips" class="tips-box">
                  <i class="el-icon-warning-outline"></i> {{ item.tips }}
                </div>
              </div>
              <el-empty v-if="!loading.speakerGuide && speakerGuideList.length === 0" description="暂无演讲者须知"></el-empty>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { venueApi, hotelApi, attractionApi, handbookApi, speakerGuideApi } from '@/api'

export default {
  name: 'Notice',
  data() {
    return {
      activeTab: 'venue',
      navItems: [
        { type: 'venue', name: '会场信息', icon: 'el-icon-office-building' },
        { type: 'hotel', name: '酒店信息', icon: 'el-icon-house' },
        // { type: 'scenic', name: '景点指南', icon: 'el-icon-place' },
        { type: 'handbook', name: '参会者手册', icon: 'el-icon-notebook-2' },
        { type: 'speaker', name: '演讲者须知', icon: 'el-icon-microphone' }
      ],
      loading: {
        venue: false,
        hotel: false,
        attraction: false,
        handbook: false,
        speakerGuide: false
      },
      venueList: [],
      hotelList: [],
      attractionList: [],
      handbookList: [],
      speakerGuideList: []
    }
  },
  watch: {
    activeTab: {
      handler(val) {
        this.loadTabData(val)
      },
      immediate: true
    }
  },
  methods: {
    loadTabData(tab) {
      switch (tab) {
        case 'venue':
          if (this.venueList.length === 0) this.fetchVenue()
          break
        case 'hotel':
          if (this.hotelList.length === 0) this.fetchHotel()
          break
        case 'scenic':
          if (this.attractionList.length === 0) this.fetchAttraction()
          break
        case 'handbook':
          if (this.handbookList.length === 0) this.fetchHandbook()
          break
        case 'speaker':
          if (this.speakerGuideList.length === 0) this.fetchSpeakerGuide()
          break
      }
    },
    async fetchVenue() {
      this.loading.venue = true
      try {
        const res = await venueApi.getList()
        this.venueList = (res.data && res.data.list) || []
      } catch (e) {
        console.error('获取会场信息失败', e)
      } finally {
        this.loading.venue = false
      }
    },
    async fetchHotel() {
      this.loading.hotel = true
      try {
        const res = await hotelApi.getList()
        this.hotelList = (res.data && res.data.list) || []
      } catch (e) {
        console.error('获取酒店信息失败', e)
      } finally {
        this.loading.hotel = false
      }
    },
    async fetchAttraction() {
      this.loading.attraction = true
      try {
        const res = await attractionApi.getList()
        this.attractionList = (res.data && res.data.list) || []
      } catch (e) {
        console.error('获取景点信息失败', e)
      } finally {
        this.loading.attraction = false
      }
    },
    async fetchHandbook() {
      this.loading.handbook = true
      try {
        const res = await handbookApi.getList()
        this.handbookList = (res.data && res.data.list) || []
      } catch (e) {
        console.error('获取手册失败', e)
      } finally {
        this.loading.handbook = false
      }
    },
    async fetchSpeakerGuide() {
      this.loading.speakerGuide = true
      try {
        const res = await speakerGuideApi.getList()
        this.speakerGuideList = (res.data && res.data.list) || []
      } catch (e) {
        console.error('获取演讲者须知失败', e)
      } finally {
        this.loading.speakerGuide = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.notice-page {
  background: #f8f9fc;
}

.notice-nav {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 28px;
  background: #fff;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(26, 35, 126, 0.1);
  }

  &.active {
    background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
    color: #fff;

    i {
      color: #ffd700;
    }
  }

  i {
    font-size: 20px;
    color: #7c4dff;
  }

  span {
    font-size: 15px;
    font-weight: 500;
  }
}

.notice-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  .card-title {
    background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
    color: #fff;
    padding: 20px 30px;
    font-size: 20px;
    display: flex;
    align-items: center;
    gap: 12px;

    i {
      font-size: 24px;
      color: #ffd700;
    }
  }

  .card-content {
    padding: 30px;

    h4 {
      font-size: 18px;
      color: #1a237e;
      margin: 24px 0 16px;

      &:first-child {
        margin-top: 0;
      }
    }

    p {
      font-size: 15px;
      color: #555;
      line-height: 1.8;
      margin-bottom: 12px;
    }

    ul {
      list-style: none;

      li {
        padding: 12px 0;
        font-size: 15px;
        color: #555;
        border-bottom: 1px dashed #eee;
        display: flex;
        align-items: center;
        gap: 10px;

        &:last-child {
          border-bottom: none;
        }

        i {
          color: #7c4dff;
          font-size: 16px;
        }
      }
    }
  }
}

.map-placeholder {
  height: 300px;
  background: #f5f7fa;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  margin-top: 24px;

  i {
    font-size: 48px;
    margin-bottom: 16px;
  }
}

// 酒店列表
.hotel-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.hotel-item {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: #f8f9fc;
  border-radius: 12px;
  transition: all 0.3s;

  &:hover {
    background: #f0f2f8;
    transform: translateX(10px);
  }

  .hotel-level {
    flex-shrink: 0;
  }

  .hotel-info {
    flex: 1;

    h4 {
      margin: 0 0 12px;
      font-size: 18px;
    }

    p {
      margin: 8px 0;
      font-size: 14px;

      i {
        margin-right: 8px;
        color: #7c4dff;
      }
    }

    .hotel-price {
      color: #f56c6c;
      font-size: 16px;
    }
  }
}

.booking-tip {
  margin-top: 24px;
}

// 景点
.scenic-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.scenic-item {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: #f8f9fc;
  border-radius: 12px;
  transition: all 0.3s;

  &:hover {
    background: linear-gradient(135deg, rgba(26, 35, 126, 0.05) 0%, rgba(124, 77, 255, 0.05) 100%);
  }

  .scenic-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #1a237e 0%, #7c4dff 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 28px;
      color: #ffd700;
    }
  }

  .scenic-info {
    h5 {
      font-size: 16px;
      color: #1a237e;
      margin-bottom: 8px;
    }

    p {
      font-size: 14px;
      color: #666;
      margin: 0;
    }
  }
}

.scenic-list {
  li {
    padding: 14px 0 !important;
  }
}

// 手册
.handbook-section {
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }

  h4 {
    font-size: 18px;
    color: #1a237e;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e0e3f0;
  }

  ul {
    margin-top: 12px;

    li {
      padding: 8px 0 !important;
      padding-left: 24px !important;
      position: relative;
      display: block !important;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 16px;
        width: 8px;
        height: 8px;
        background: #7c4dff;
        border-radius: 50%;
      }
    }
  }
}

// 会场信息
.venue-item {
  padding: 20px;
  background: #f8f9fc;
  border-radius: 12px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  h4 {
    margin-top: 0;
  }
}

// 景点额外信息
.scenic-address,
.scenic-price,
.scenic-time {
  font-size: 13px !important;
  color: #888 !important;
  margin-top: 8px !important;

  i {
    margin-right: 6px;
    color: #7c4dff;
  }
}

// 下载链接
.download-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  color: #fff;
  border-radius: 8px;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(26, 35, 126, 0.3);
  }

  i {
    font-size: 16px;
  }
}

// 提示框
.tips-box {
  margin-top: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
  border-radius: 8px;
  color: #e65100;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;

  i {
    font-size: 18px;
    margin-top: 2px;
  }
}

@media screen and (max-width: 768px) {
  .notice-nav {
    gap: 10px;
  }

  .nav-item {
    padding: 12px 20px;

    span {
      display: none;
    }
  }

  .scenic-grid {
    grid-template-columns: 1fr;
  }
}
</style>
